import React, { useState, useEffect, useRef, useMemo } from "react";
import api from "../Utils/api";
import { toast } from "react-toastify";
import { MoreVertical } from "lucide-react";
import UserAvatar from "../Components/User/UserAvatar";
import UserProfileDialog from "../Components/User/UserProfileDialog";
import UserAddDialog from "../Components/User/AddUserModal";
import DropDownMenu from "../Components/User/Dropdown";
import { Search } from "lucide-react";
import PersonalDetailsDialog from "../Components/User/PersonalDetails";
import UserMenu from "../Components/User/UserMenu";
import PersonalDetailsForm from "../Components/User/PersonalDetails";
import FilterPopover from "../Components/User/FilterPopover";
import DisplayDropdown from "../Components/User/DislpayButton";
import { accessMap, employmentTypes, stressProfileMap } from "../Utils/const";
import { setUser } from "../Store/Actions/userActions";
import { useSelector } from "react-redux";
import Spinner from "../Components/Spinner";

const People = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [modify, setModify] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // const [currentPage, setCurrentPage] = useState(1);

  const [selectedUserID, setSelectedUserID] = useState("");

  const [openEmployment, setOpenEmployment] = useState(false);
  const [openPersonal, setOpenPersonal] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [pendingUserID, setPendingUserID] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const user = useSelector((state) => state.user).user;

  const [displayedFields, setDisplayedFields] = useState([
    "Username",
    "Main Location",
    "Email",
    "Mobile",
    "Status",
  ]);

  const [filters, setFilters] = useState({
    status: "employed",
    location: "All",
  });

  const [tableDisplay, setTableDisplay] = useState({
    main_location: true,
    email: true,
    mobile: true,
    username: true,
  });

  const StatusMap = {
    all: true,
    employed: true,
    archived: false,
  };


  useEffect(() => {
    const newTableDisplay = {};
    displayedFields.forEach((label) => {
      const key = labelToKey(label);
      newTableDisplay[key] = displayedFields.includes(label);
    });

    setTableDisplay(newTableDisplay);
  }, [displayedFields]);

  useEffect(() => {
    // Filter logic whenever filters change
    const filtered = users.filter((person) => {
      // Status filter
      const matchStatus =
        filters.status === "all" ||
        (filters.status === "employed" && person.archive === false) ||
        (filters.status === "archived" && person.archive === true);

      // Location filter
      const matchLocation =
        filters.location === "All" ||
        person.locations?.primaryLocation?._id === filters.location;

      // Combine all filters
      return matchStatus && matchLocation;
    });

    setFilteredPeoples(filtered);
  }, [filters, users]);

  const showPersonalInfoEdit = (id) => {
    setSelectedUserID(id);
    setOpenPersonal(true);
  };

  const dropdownRefs = useRef({});

  useEffect(() => {
    getUsers();
    getLocations();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownIndex !== null &&
        dropdownRefs.current[dropdownIndex] &&
        !dropdownRefs.current[dropdownIndex].contains(event.target)
      ) {
        setDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownIndex]);

  const handleModifyUser = (index, id) => {
    setModify(true);
    setSelectedUserID(id);
  };

  const labelToKey = (label) => {
    // Map label to your tableDisplay key format
    switch (label) {
      case "Username":
        return "username";
      case "Main Location":
        return "main_location";
      case "Email":
        return "email";
      case "Mobile":
        return "mobile";
      case "Status":
        return "status";
      default:
        return label.toLowerCase().replace(/\s+/g, "_");
    }
  };

  const getUsers = async () => {
    try {
      const response = await api.get("/user");
      setUsers(response.data.users);
      setFilteredPeoples(response.data.users);
      setCurrentPage(1); // reset page on new data
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch users");
    }
  };

  const closeEmploymentDetails = () => {
    getUsers();
    setOpenEmployment(false);
  };

  const closePersonalDetails = () => {
    getUsers();
    setOpenPersonal(false);
  };

  const toggleUserAllowBlock = async (userID, allow) => {
    if (allow == undefined || allow == null) {
      allow = false;
    }
    const res = await api.post("/user/update", {
      _id: userID,
      updatedData: {
        allowed: !allow,
      },
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getUsers();
    } else {
      toast.error(res.data.message);
    }
  };

  const getLocations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/location");
      setIsLoading(false);
      setLocations(response.data.locations.filter((loc) => !loc.archive));
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch locations");
    }
  };

  const showArchiveConfirmation = (userID) => {
    setPendingUserID(userID);
    setShowArchiveModal(true);
  };

  const showRestoreConfirmation = (userID) => {
    setPendingUserID(userID);
    setShowRestoreModal(true);
  };

  const archiveUser = async () => {
    if (!pendingUserID) return;

    try {
      const res = await api.post(`/user/archive`, {
        userID: pendingUserID,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        getUsers();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to delete user");
      console.error(error);
    } finally {
      setShowArchiveModal(false);
      setPendingUserID(null);
    }
  };

  const restoreUser = async () => {
    if (!pendingUserID) return;

    try {
      const res = await api.post(`/user/restore`, {
        userID: pendingUserID,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        getUsers();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to restore user");
      console.error(error);
    } finally {
      setShowRestoreModal(false);
      setPendingUserID(null);
    }
  };

  const getLocationName = (locationID) => {
    const loc = locations.find((l) => l._id === locationID);
    return loc ? loc.name : "N/A";
  };

  const [filteredPeoples, setFilteredPeoples] = useState(users);

  const handlePeopleSearch = () => {
    if (searchTerm !== "") {
      const filteredPeopleData = users.filter((user) => {
        const search = searchTerm.toLowerCase();
        return (
          (user.firstName && user.firstName.toLowerCase().includes(search)) ||
          (user.lastName && user.lastName.toLowerCase().includes(search))
        );
      });
      setFilteredPeoples(filteredPeopleData);
    } else {
      const filtered = users.filter((person) => {
        // Status filter
        const matchStatus =
          filters.status === "All" ||
          person.allowed === StatusMap[filters.status];

        // Location filter
        const matchLocation =
          filters.location === "All" ||
          person.locations?.primaryLocation?._id === filters.location;

        // Combine all filters
        return matchStatus && matchLocation;
      });

      setFilteredPeoples(filtered);
    }
  };

  const handlePeopleKeyDown = (e) => {
    if (e.key === "Enter") {
      handlePeopleSearch();
    }
  };

  //-------------pagination--------------------
  const [currentPage, setCurrentPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(30);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredPeoples.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(users.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const actionOptions = ["View", "Edit Personal", "Edit Employment"];

  const handleSelectAction = (value) => {};

  const AddModalClose = () => {
    setOpenAddUserModal(false);
    getUsers();
  };

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">Staff</h1>
        <button
          onClick={() => setOpenAddUserModal(true)}
          className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600 transition"
        >
          + Add New Staff
        </button>
      </div>

      <div className="pt-2">
        <div className="min-w-full !min-h-[55px] flex justify-between items-start pl-2">
          <ul className="w-full flex items-center space-x-2 font-[sans-serif] mt-2">
            <li
              className={`w-[300px] text-gray-500 bg-[transparent] text-sm tracking-wide flex items-center rounded cursor-pointer hover:!border-[#265c7f] bg-[transparent]`}
            >
              <div className="w-full relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="!w-full border border-gray-500 rounded-md py-1.5 px-4 pl-10 focus:outline-none focus:ring-1 focus:ring-gray-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handlePeopleKeyDown}
                />
                <Search
                  className="absolute left-3 top-2.5 text-gray-500"
                  size={16}
                />
              </div>
            </li>
            <li
              className={` bg-gray-500 px-4 py-2 text-sm tracking-wide flex items-center rounded hover:bg-gray-700 cursor-pointer hover:!border-[#265c7f]`}
              onClick={() => handlePeopleSearch()}
            >
              <span className="text-[white]">Search</span>
            </li>
            <li>
              <FilterPopover
                locations={locations.map((loc) => {
                  return {
                    name: loc.name,
                    id: loc._id,
                  };
                })}
                filters={filters}
                onFilterChange={setFilters}
              />
            </li>
            <li>
              <DisplayDropdown
                selected={displayedFields}
                onChange={setDisplayedFields}
              />
            </li>
          </ul>
          <div>
            <div
              className="h-[50px] flex items-center mt-[12px] pl-3"
              style={{ fontFamily: "Liberation Serif" }}
            >
              <div className="flex whitespace-nowrap text-[#303133] font-bold">
                <span className="text-[13px] min-w-[35.5px] h-[28px] leading-[28px] align-top mr-2.5 font-normal text-gray-600">
                  Totally {users && users.length} Items
                </span>
                <div className="flex justify-center items-center">
                  {/* <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-[30px] h-[28px] bg-[#f4f4f5] rounded-md hover:cursor-pointer disabled:cursor-not-allowed mr-2 group pl-2"
                  >
                    <svg
                      className="w-3 fill-[#606266] group-disabled:fill-gray-300"
                      viewBox="0 0 55.753 55.753"
                    >
                      <path
                        d="M12.745 23.915c.283-.282.59-.52.913-.727L35.266 1.581a5.4 5.4 0 0 1 7.637 7.638L24.294 27.828l18.705 18.706a5.4 5.4 0 0 1-7.636 7.637L13.658 32.464a5.367 5.367 0 0 1-.913-.727 5.367 5.367 0 0 1-1.572-3.911 5.369 5.369 0 0 1 1.572-3.911z"
                        data-original="#000000"
                      />
                    </svg>
                  </button>

                  <div className="flex space-x-2">
                    {[...Array(totalPages).keys()].map((number) => (
                      <button
                        key={number}
                        onClick={() => handlePageChange(number + 1)}
                        className={`w-[30px] h-[28px] rounded-md text-sm font-medium ${
                          currentPage === number + 1
                            ? "bg-gray-500 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {number + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-[30px] h-[28px] bg-[#f4f4f5] rounded-md hover:cursor-pointer disabled:cursor-not-allowed ml-3 items-center group pl-2"
                  >
                    <svg
                      className="w-3 fill-[#606266] group-disabled:fill-gray-300 rotate-180"
                      viewBox="0 0 55.753 55.753"
                    >
                      <path
                        d="M12.745 23.915c.283-.282.59-.52.913-.727L35.266 1.581a5.4 5.4 0 0 1 7.637 7.638L24.294 27.828l18.705 18.706a5.4 5.4 0 0 1-7.636 7.637L13.658 32.464a5.367 5.367 0 0 1-.913-.727 5.367 5.367 0 0 1-1.572-3.911 5.369 5.369 0 0 1 1.572-3.911z"
                        data-original="#000000"
                      />
                    </svg>
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full h-[calc(100vh-250px)] rounded-md mb-20  overflow-y-auto mt-2">
        <table className="w-full whitespace-nowrap mb-[100px]">
          <thead className="text-left sticky top-0 z-10 bg-gray-200 text-md font-semibold text-gray-700 ">
            <tr>
              <th
                className="p-4 cursor-pointer select-none"
                title="Sort by Name"
              >
                Name
              </th>
              <th
                className="p-4 cursor-pointer select-none"
                title="Sort by Name"
              >
                Access
              </th>
              <th
                className="p-4 cursor-pointer select-none"
                title="Company"
              >
                Company
              </th>
              {tableDisplay.username && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Username"
                >
                  Username
                </th>
              )}
              {tableDisplay.main_location && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Name"
                >
                  Main Location
                </th>
              )}
              {tableDisplay.email && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Email"
                >
                  Email
                </th>
              )}
              {tableDisplay.mobile && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Phone"
                >
                  Mobile
                </th>
              )}
              {tableDisplay.status && <th className="p-4">Allowed</th>}
              <th className="p-4 w-16 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="border-b">
            {filteredPeoples.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-4 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            ) : (
              filteredPeoples.map((user, index) => (
                <tr
                  key={user._id || index}
                  className="border-t text-sm text-gray-800 hover:bg-gray-50"
                >
                  <td className="p-4 flex h-24 items-center">
                    <UserAvatar user={user} />
                    <div>
                      <div className="font-medium text-gray-700">
                        {user.firstName} {user.lastName}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {accessMap[user.deputyAccessLevel] || "employee"}
                  </td>
                  <td className="p-4 text-gray-600">
                    {user.companyID?.name || "-"}
                  </td>
                  {tableDisplay.username && (
                    <td className="p-4">
                      {user.preferredName || "N/A"}
                    </td>
                  )}
                  {tableDisplay.main_location && (
                    <td className="p-4">
                      {user?.locations?.primaryLocation?.name || ""}
                    </td>
                  )}
                  {tableDisplay.email && (
                    <td className="p-4 break-all text-gray-500">
                      {user.email || ""}
                    </td>
                  )}
                  {tableDisplay.mobile && (
                    <td className="p-4">{user.phone || "N/A"}</td>
                  )}
                  {tableDisplay.status && (
                    <td className="p-4">
                      <button
                        onClick={() =>
                          toggleUserAllowBlock(user._id, user.allowed)
                        }
                        className={`inline-flex items-center gap-1 font-medium p-2 rounded ${
                          user.allowed
                            ? "text-green-600 hover:bg-green-200"
                            : "text-red-600 hover:bg-red-200"
                        }`}
                        type="button"
                      >
                        {user.allowed ? (
                          <>
                            <i
                              className="fa fa-check-circle mr-2"
                              aria-hidden="true"
                            ></i>
                            Allowed
                          </>
                        ) : (
                          <>
                            <i
                              className="fa fa-times-circle mr-2"
                              aria-hidden="true"
                            ></i>
                            Blocked
                          </>
                        )}
                      </button>
                    </td>
                  )}
                  <td className="p-4 text-center space-x-2">
                    <UserMenu
                      archived={user.archive}
                      onEditPersonal={() => showPersonalInfoEdit(user._id)}
                      onArchive={() => showArchiveConfirmation(user._id)}
                      onRestore={() => showRestoreConfirmation(user._id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* <Pagination /> */}

      <PersonalDetailsDialog
        id={selectedUserID}
        open={openEmployment}
        onClose={closeEmploymentDetails}
        locations={locations}
      />

      <PersonalDetailsForm
        id={selectedUserID}
        open={openPersonal}
        locations={locations}
        onClose={closePersonalDetails}
      />

      {openView && (
        <UserProfileDialog
          onClose={() => setOpenView(false)}
          userID={selectedUserID}
          locations={locations}
          open={openView}
        />
      )}

      {modify && (
        <UserProfileDialog
          id={selectedUserID}
          onClose={() => setModify(false)}
          locations={locations}
        />
      )}
      {openAddUserModal && (
        <UserAddDialog onClose={() => AddModalClose()} locations={locations} />
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-700">Archive Staff</h2>
              <button
                onClick={() => {
                  setShowArchiveModal(false);
                  setPendingUserID(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-base text-gray-700">
                Are you sure you want to archive this staff member? This will remove them from the active staff list.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowArchiveModal(false);
                  setPendingUserID(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={archiveUser}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-700">Restore Staff</h2>
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setPendingUserID(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-base text-gray-700">
                Are you sure you want to restore this staff member? They will be added back to the active staff list.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setPendingUserID(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={restoreUser}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default People;

function renderPayRates(employmentType, payRates) {
  // If payRates is missing, show nothing
  if (!payRates) return null;

  // Fixed Rate
  if (payRates.payRateType === "fixed_rate") {
    return (
      <div>
        <div>
          <span className="font-medium">Fixed Rate:</span> €{payRates.fixedRate}
        </div>
      </div>
    );
  }

  // Salary
  if (payRates.payRateType === "salary") {
    return (
      <div>
        <div>
          <span className="font-medium">Salary:</span> €{payRates.salaryAmount}{" "}
          ({payRates.salaryPeriod})
        </div>
      </div>
    );
  }

  // Hourly or similar
  return (
    <div>
      <div>
        <span className="font-medium">Weekday:</span> €{payRates.weekday}
      </div>
      <div>
        <span className="font-medium">Saturday:</span> €{payRates.saturday}
      </div>
      <div>
        <span className="font-medium">Sunday:</span> €{payRates.sunday}
      </div>
      <div>
        <span className="font-medium">Public Holiday:</span> €
        {payRates.publicHoliday}
      </div>
    </div>
  );
}
